import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useBlocker } from 'react-router-dom';
import { ArrowLeft, Save, ImagePlus, X, Eye } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getImageUrl, cleanHtmlContent, createQuillImageHandler, generateSlug, processHtmlAndUploadImages, fixHtmlImageUrls } from '../../utils/helpers';
import { toast } from 'react-toastify';

const lineHeights = ['1.0', '1.2', '1.5', '1.8', '2.0', '2.5', '3.0'];
try {
    const Parchment = Quill.import('parchment');
    const StyleClass = Quill.import('attributors/style/align') || Quill.import('attributors/style/direction');
    
    if (StyleClass && typeof StyleClass.constructor === 'function') {
        const LineHeightStyle = new StyleClass.constructor('lineHeight', 'line-height', {
            scope: Parchment.Scope.BLOCK,
            whitelist: lineHeights,
        });
        Quill.register(LineHeightStyle, true);
    }

    const icons = Quill.import('ui/icons');
    icons['unlink'] = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" x2="16" y1="12" y2="12"/><line x1="2" x2="22" y1="2" y2="22"/></svg>`;

} catch (error) {
    console.error("Không thể đăng ký LineHeightStyle cho Quill:", error);
}

const AdminNewsEdit = () => {
    const { id } = useParams(); 
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const quillRef = useRef(null);

    const [initialLoading, setInitialLoading] = useState(true);
    const [categories, setCategories] = useState([]);

    const [formData, setFormData] = useState({ newsTitle: '', newsExcerpt: '' , categoryId: '', isFeatured: false, newsStatus: true});
    const [content, setContent] = useState('');
    const [image, setImage] = useState({ file: null, preview: '', isExisting: false });
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [resNews, resCats] = await Promise.all([
                    adminStaffService.getNewsDetail(id).catch(() => null),
                    adminStaffService.getAllNewsCategories().catch(()=>[])
                ]);
                
                setCategories(resCats?.data || resCats || []);
                const newsData = resNews?.data || resNews;
                
                if(!newsData) {
                    toast.error("Không tìm thấy bài viết!");
                    navigate('/admin/content');
                    return;
                }
                
                setFormData({
                    newsTitle: newsData.newsTitle || '',
                    newsExcerpt: newsData.newsExcerpt || '',
                    categoryId: newsData.categoryId || '',
                    newsStatus: newsData.newsStatus ?? true,
                    isFeatured: newsData.isFeatured || false
                });
                
                // 👉 ĐÃ SỬA: Tiền xử lý HTML trước khi đưa vào Quill
                let loadedContent = newsData.newsContent || '';
                loadedContent = loadedContent
                    .replace(/\u2003/g, '&nbsp;') 
                    .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
                    .replace(/ {2}/g, '&nbsp; '); 

                setContent(loadedContent);
                
                if (newsData.newsImageUrl || newsData.newsImage) {
                    setImage({ file: null, preview: getImageUrl(newsData.newsImageUrl || newsData.newsImage), isExisting: true });
                }
            } catch (error) {
                toast.error("Không tìm thấy bài viết hoặc có lỗi hệ thống!");
                navigate('/admin/content');
            } finally {
                setInitialLoading(false);
            }
        };
        fetchInitialData();
    }, [id, navigate]);

    const getModules = () => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }], 
                [{ 'font': [] }], 
                [{ 'align': ['', 'center', 'right', 'justify'] }], 
                [{ 'lineHeight': lineHeights }], 
                ['bold', 'italic', 'underline', 'strike', 'blockquote'], 
                [{ 'list': 'ordered' }, { 'list': 'bullet' }], 
                [{ 'indent': '-1' }, { 'indent': '+1' }], 
                [{ 'color': [] }, { 'background': [] }], 
                ['link', 'unlink', 'image', 'video'], 
                ['clean'] 
            ],
            handlers: {
                lineHeight: function (value) {
                    if (value) {
                        this.quill.format('lineHeight', value);
                    } else {
                        this.quill.format('lineHeight', '1.5'); 
                    }
                },
                unlink: function() {
                    this.quill.format('link', false);
                },
                image: createQuillImageHandler(quillRef, adminStaffService.uploadEditorImage, 'image_news')
            }
        },
        clipboard: {
            matchVisual: false 
        }
    });

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setIsDirty(true); 
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage({ file, preview: URL.createObjectURL(file), isExisting: false });
            setIsDirty(true);
        }
    };
    
    const blocker = useBlocker(() => isDirty);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) { e.preventDefault(); e.returnValue = ''; }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content || content === '<p><br></p>') {
            toast.warn("Vui lòng nhập nội dung bài viết!");
            return;
        }
        setLoading(true);
        const toastId = toast.loading("Đang xử lý và cập nhật bài viết...");
        
        try {
            const cleanContent = await processHtmlAndUploadImages(
                content, 
                'image_news', 
                adminStaffService.uploadEditorImage,
                adminStaffService.uploadExternalEditorImage
            );

            setContent(cleanContent);

            const submitData = new FormData();
            const newsDataJson = JSON.stringify({
                newsTitle: formData.newsTitle,
                newsExcerpt: formData.newsExcerpt,
                categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
                newsContent: cleanHtmlContent(cleanContent),
                newsStatus: formData.newsStatus,
                isFeatured: formData.isFeatured
            });

            submitData.append("data", newsDataJson);
            if (image.file) submitData.append("image", image.file);
            setIsDirty(false);

            await adminStaffService.updateNews(id, submitData);
            
            toast.update(toastId, { render: "Cập nhật bài viết thành công!", type: "success", isLoading: false, autoClose: 2000 });
        } catch (error) {
            console.error("Lỗi cập nhật bài:", error);
            toast.update(toastId, { render: "Có lỗi xảy ra khi cập nhật!", type: "error", isLoading: false, autoClose: 3000 });
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) return <div className="p-10 text-center text-tnc-orange font-bold">Đang tải dữ liệu bài viết...</div>;

    return (
        <div className="max-w-6xl mx-auto pb-10">
            <style dangerouslySetInnerHTML={{__html: `
                .ql-snow .ql-picker.ql-lineHeight { width: 110px; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-label { padding-right: 20px; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-label::before { content: 'Giãn dòng'; padding-right: 5px; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.0"]::before { content: '1.0'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.2"]::before { content: '1.2'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.5"]::before { content: '1.5'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.8"]::before { content: '1.8'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.0"]::before { content: '2.0'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.5"]::before { content: '2.5'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="3.0"]::before { content: '3.0'; }
                
                .ql-snow .ql-toolbar button.ql-unlink svg {
                    width: 18px;
                    height: 18px;
                }
                .ql-snow .ql-toolbar button.ql-unlink {
                    color: #444;
                }
                .ql-snow .ql-toolbar button.ql-unlink:hover {
                    color: #06c;
                }
            `}} />

            <div className="flex items-center justify-between mb-6 bg-gray-50 py-4 px-2 border-b sticky top-0 z-50 shadow-sm">
                <div className="flex items-center gap-4">
                    {/* 👉 ĐÃ SỬA MÀU: hover:text-tnc-orange */}
                    <Link to="/admin/content" className="p-2 bg-white rounded-md shadow-sm text-gray-500 hover:text-tnc-orange transition-colors"><ArrowLeft size={20} /></Link>
                    {/* 👉 ĐÃ SỬA MÀU: text-tnc-orange */}
                    <h2 className="text-2xl font-bold text-gray-800">Sửa bài viết <span className="text-tnc-orange">#{id}</span></h2>
                </div>
                <div className="flex items-center gap-3">
                    <Link 
                        to={formData.newsTitle ? `/tin-tuc/${generateSlug(formData.newsTitle)}-id${id}` : '#'} 
                        target="_blank" 
                        className="flex items-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 px-5 py-2.5 rounded-md font-bold transition-colors shadow-sm"
                        title={formData.newsTitle ? "Xem trên giao diện khách hàng" : "Vui lòng nhập tên bài viết trước"}
                    >
                        <Eye size={18}/> XEM THỰC TẾ
                    </Link>
                    
                    {/* 👉 ĐÃ SỬA MÀU: bg-tnc-orange hover:bg-tnc-orange-hover */}
                    <button onClick={handleSubmit} disabled={loading} className="flex items-center gap-2 bg-tnc-orange hover:bg-tnc-orange-hover text-white px-6 py-2.5 rounded-md font-bold transition-colors shadow-sm disabled:bg-gray-400">
                        {loading ? <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span> : <Save size={20} />}
                        LƯU CẬP NHẬT
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <div className="mb-4">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Tiêu đề bài viết *</label>
                            {/* 👉 ĐÃ SỬA MÀU: focus:border-tnc-orange */}
                            <input type="text" name="newsTitle" required value={formData.newsTitle} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange font-bold text-lg" />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Chuyên mục tin tức</label>
                            {/* 👉 ĐÃ SỬA MÀU: focus:border-tnc-orange */}
                            <select 
                                name="categoryId" 
                                value={formData.categoryId} 
                                onChange={handleInputChange} 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange font-medium bg-gray-50"
                            >
                                <option value="">-- Chọn chuyên mục (Tùy chọn) --</option>
                                {categories.map(cat => (
                                    <option key={cat.categoryId} value={cat.categoryId}>{cat.categoryName}</option>
                                ))}
                            </select>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center justify-between">
                                <span>Đoạn trích dẫn (Mô tả ngắn)</span>
                                <span className="text-xs font-normal text-gray-400 italic">Không bắt buộc</span>
                            </label>
                            {/* 👉 ĐÃ SỬA MÀU: focus:border-tnc-orange */}
                            <textarea name="newsExcerpt" rows="3" value={formData.newsExcerpt} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange resize-none"></textarea>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-bold text-gray-700">Nội dung chi tiết *</label>
                            </div>
                            <div className="border border-gray-300 rounded-lg">
                                <ReactQuill 
                                    ref={quillRef} 
                                    theme="snow" 
                                    // 👉 ĐÃ SỬA: Thêm fixHtmlImageUrls vào thuộc tính value
                                    value={content ? fixHtmlImageUrls(content) : ''} 
                                    onChange={setContent} 
                                    modules={getModules()}
                                    className="bg-white [&_.ql-container]:min-h-[500px] [&_.ql-editor]:text-base [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-toolbar]:bg-gray-50 [&_.ql-container]:border-none pb-1" 
                                    placeholder="Soạn thảo..." 
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 top-24">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Ảnh đại diện (Thumbnail)</h3>
                        <div className="flex flex-col items-center gap-4">
                            {image.preview ? (
                                <div className="relative w-full aspect-video rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                    <img src={image.preview} alt="Preview" className={`w-full h-full object-cover ${image.isExisting ? 'opacity-90' : ''}`} />
                                    {/* 👉 ĐÃ SỬA MÀU: bg-tnc-orange hover:bg-tnc-orange-hover */}
                                    <button type="button" onClick={() => setImage({ file: null, preview: '', isExisting: false })} className="absolute top-2 right-2 bg-tnc-orange text-white rounded-full p-1.5 hover:bg-tnc-orange-hover transition-colors shadow-md"><X size={16}/></button>
                                </div>
                            ) : (
                                <label className="w-full aspect-video border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:bg-orange-50 hover:border-tnc-orange hover:text-tnc-orange cursor-pointer transition-colors">
                                    <ImagePlus size={32} className="mb-2" />
                                    <span className="text-sm font-medium">Click để tải ảnh mới lên</span>
                                    <span className="text-xs text-gray-400 mt-1">Khuyên dùng tỉ lệ 16:9</span>
                                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                </label>
                            )}
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mt-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Cài đặt hiển thị</h3>
                        <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors mb-3">
                            <input type="checkbox" checked={formData.newsStatus} onChange={(e) => setFormData({...formData, newsStatus: e.target.checked})} className="w-5 h-5 text-blue-600 rounded cursor-pointer" />
                            <div><span className="font-bold text-gray-800 block text-sm">Cho phép hiển thị</span></div>
                        </label>
                        {/* 👉 ĐÃ SỬA MÀU: hover:bg-orange-50 */}
                        <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-orange-50 transition-colors">
                            {/* 👉 ĐÃ SỬA MÀU: text-tnc-orange */}
                            <input type="checkbox" checked={formData.isFeatured} onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})} className="w-5 h-5 text-tnc-orange rounded cursor-pointer" />
                            <div><span className="font-bold text-gray-800 block text-sm">Tin tức nổi bật</span></div>
                        </label>
                    </div>
                </div>
            </div>

            {blocker.state === 'blocked' && (
                <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Rời khỏi trang?</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Bạn có thay đổi chưa được lưu. Nếu rời đi, các thay đổi này sẽ bị mất.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => blocker.reset()} className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-lg border border-gray-200">
                                Ở lại
                            </button>
                            <button onClick={() => blocker.proceed()} className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg">
                                Rời đi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminNewsEdit;